import {Contact} from "./types";
import dayjs from "dayjs";
import "dayjs/locale/da"

const ACTIVE = 'ACTIVE';
const ORDER_UP = 'ORDER_UP'
const ORDER_DOWN = 'ORDER_DOWN'
const CANCEL = 'CANCEL'
const FINISH = 'FINISH'

declare global {
    interface String {
        splitAt(index : number) : [string, string];
    }
}

String.prototype.splitAt = function (index): [string, string] {
    return [this.slice(0, index), this.slice(index)]
}

export const fullname = (customer_contact: Contact): string => {
    return customer_contact.first_name + ' ' + customer_contact.last_name
}

export const address = (customer_contact: Contact): string => {
    const {street_name, street_number, street_letter, street_floor, street_side, street_zip, city} = customer_contact

    let result = street_name + ' ' + street_number + (street_letter ?? '')
    if (street_floor || street_side) {
        result += ', ';
    }

    if (street_floor) {
        result += street_floor;
        if (!isNaN(Number(street_floor))) {
            result += '.';
        }
        if (street_side) {
            result += ' ';
        }
    }

    if (street_side) {
        result += street_side;
    }

    if (city) {
        result += ', ' + street_zip + ' ' + city
    } else {
        result += ', ' + street_zip;
    }

    return result;
}

export const addressShortened = (customer_contact: Contact): string => {
    const {street_name, street_number, street_letter, street_floor, street_side} = customer_contact

    let result = street_name + ' ' + street_number + (street_letter ?? '')
    if (street_floor || street_side) {
        result += ', ';
    }

    if (street_floor) {
        result += street_floor;
        if (!isNaN(Number(street_floor))) {
            result += '.';
        }
        if (street_side) {
            result += ' ';
        }
    }

    if (street_side) {
        result += street_side;
    }

    return result;
}

export const phoneNumber = (phone_number: string): string => {
    return phone_number
        .replace(/^(0045)/, '')
        .replace(/^(\\+45)/, '')
        .splitAt(2)
        .join(' ')
        .splitAt(5)
        .join(' ')
        .splitAt(8)
        .join(' ')
}

export function formatAmount(amount: string|number|null, pretty: boolean = true): string {
    if (!amount) {
        if (pretty) {
            return '0,-';
        } else {
            return '0';
        }
    }

    const amountNumber = Number(amount);
    let formattedAmount = amountNumber.toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    if (pretty) {
        formattedAmount = formattedAmount.replace(',00', ',-');
    } else {
        formattedAmount = formattedAmount.replace(',00', '');
    }

    return formattedAmount;
}

export function formatAmountWithCurrency(amount: string|number, pretty = false, currency = 'kr.'): string {
    return `${formatAmount(amount, pretty)} ${currency}`;
}

export function formatAmountWithCurrencyOverPeriod(amount: string|number, pretty = false, currency = 'kr.', period = 'md.'): string {
    return `${formatAmount(amount, pretty)} ${currency}/${period}`;
}

export function formatAmountOverPeriod(amount: string|number, pretty = true, period = 'md.'): string {
    return `${formatAmount(amount, pretty)}/${period}`;
}

export function formatPrice(amount: number): string {
    return amount.toLocaleString('da-DK')
}

export function formatCardExpiration(expiration: string): string {
    const splitted = expiration.split("-");
    const month = splitted[0].length === 1 ? "0" + splitted[0] : splitted[0];
    const year = splitted[1].substring(2);
    return month + "/" + year;
}

export function formatPeriod(start: string, end: string): string {
    return `${dayjs(start).locale('da').format('DD/MM-YY')}` + (end ? ` – ${dayjs(end).locale('da').format('DD/MM-YY')}` : '');
}

export function formatDate(date: string, format: string = 'DD/MM-YY'): string {
    return `${dayjs(date).locale('da').format(format)}`;
}

export function formatDateText(date: string, format: string = 'D. MMMM YYYY'): string {
    return `${dayjs(date).locale('da').format(format)}`;
}

export function formatDateTime(date: string, format: string = 'DD/MM-YY HH:mm'): string {
    return formatDate(date, format);
}

export function translateCustomerStatus(status: string): string {
    switch (status) {
        case ACTIVE:
            return 'Aktiv'
        case ORDER_UP:
            return 'Under Oprettelse'
        case CANCEL:
            return 'Annulleret'
        case FINISH:
        case ORDER_DOWN:
            return 'Opsagt'
        default:
            return ''
    }
}
